/**
 * Haul.Taxi — what a job's location looks like to the PUBLIC versus to the people entitled to it.
 *
 * WHY THIS EXISTS. Sharetribe listing `publicData` and `geolocation` are world-readable: anyone can
 * call `listings.query` with the client id that ships in our JS bundle — no login, no listing URL —
 * and read every field for every job. Storing a street address there meant the whole board of open
 * jobs was an enumerable address list. Restricting `fields.listing` in our own ducks does NOT help,
 * because the browser talks to the Marketplace API directly and can ask for whatever it likes.
 *
 * So the split is made where it is actually enforced — at WRITE time:
 *   • publicData.location  → { city, state } only. Enough for a card, a filter and a map region.
 *   • privateData.location → { address, building, lat, lng }. Author-only in the Marketplace API;
 *                            released to the WINNING hauler through a privileged server endpoint.
 *   • geolocation          → a FUZZED point. It has to stay public for location search to work, but
 *                            an exact coordinate is just the address in another form.
 *
 * NOTE on Sharetribe's own `config.maps.fuzzy`: that setting only offsets the pin at RENDER time —
 * the true coordinate is still in the payload (its own comment says so). It is a display nicety, not
 * a privacy control. This module offsets what is actually STORED, which is the part that matters.
 */

// Roughly a 4-5 minute walk. Big enough that the point no longer identifies a building, small enough
// that "jobs near me" still means something.
export const FUZZ_OFFSET_METERS = 500;

const EARTH_RADIUS_METERS = 6378137;
const DEG_PER_RAD = 180 / Math.PI;

/**
 * Offset a coordinate by a random vector inside a circle of `offsetMeters`.
 *
 * Uniform over the DISC (hence the sqrt on the radius) rather than over the radius — sampling the
 * radius linearly would cluster points near the centre and leave the true location the most likely
 * single answer, which is precisely what we are trying to avoid.
 *
 * ⚠️ CALL THIS ONCE PER TRUE COORDINATE, NOT ONCE PER SAVE. Each call is an independent sample, so
 * publishing a fresh one on every save hands an observer a series whose mean converges on the true
 * point (~25 saves gets within roughly 70m). `stableFuzzyGeolocation` is what callers should use:
 * it reuses the stored offset until the real coordinate actually changes.
 *
 * @param {{lat: number, lng: number}} origin the true coordinate
 * @param {number} [offsetMeters]
 * @returns {{lat: number, lng: number}|null} the fuzzed point, or null if the input isn't usable
 */
export const fuzzyGeolocation = (origin, offsetMeters = FUZZ_OFFSET_METERS) => {
  const lat = origin?.lat;
  const lng = origin?.lng;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  const angle = Math.random() * 2 * Math.PI;
  const radius = Math.sqrt(Math.random()) * offsetMeters;

  const dLat = ((radius * Math.cos(angle)) / EARTH_RADIUS_METERS) * DEG_PER_RAD;
  // Degrees of longitude shrink toward the poles, so scale by cos(lat) to keep the offset metric.
  // Guard the cosine: at the poles it tends to 0 and would blow the longitude offset up to infinity.
  const cosLat = Math.cos(lat / DEG_PER_RAD);
  const dLng =
    Math.abs(cosLat) < 1e-6
      ? 0
      : ((radius * Math.sin(angle)) / (EARTH_RADIUS_METERS * cosLat)) * DEG_PER_RAD;

  // Clamp latitude to the valid range; wrap longitude across the antimeridian rather than emitting
  // an out-of-range value the maps layer would reject.
  const fuzzedLat = Math.max(-90, Math.min(90, lat + dLat));
  let fuzzedLng = lng + dLng;
  if (fuzzedLng > 180) fuzzedLng -= 360;
  if (fuzzedLng < -180) fuzzedLng += 360;

  return { lat: fuzzedLat, lng: fuzzedLng };
};

// Full US state/territory names → their postal codes. Needed because the marketplace geocodes with
// MAPBOX, which formats the state segment as a FULL NAME ("…, Boston, Massachusetts 02108, United
// States") rather than the two-letter code Google Places returns. A code-only parser silently failed
// on every real address — which is why the card's city row never appeared for genuine listings.
const US_STATE_CODES = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
  'district of columbia': 'DC',
  'puerto rico': 'PR',
  'u.s. virgin islands': 'VI',
  guam: 'GU',
};

/**
 * The fuzzed point to publish for a given true coordinate, reusing the one already stored whenever
 * the true coordinate has not moved.
 *
 * This is the function write paths should call. Re-sampling on every save would let anyone who
 * polls the public listing average the offsets away and recover the real location; holding the
 * offset steady means repeated saves disclose nothing new.
 *
 * @param {{lat: number, lng: number}} trueOrigin the real coordinate being saved
 * @param {Object} [storedPrivateLocation] the listing's existing privateData.location, if any
 * @returns {{lat: number, lng: number}|null}
 */
export const stableFuzzyGeolocation = (trueOrigin, storedPrivateLocation) => {
  const lat = trueOrigin?.lat;
  const lng = trueOrigin?.lng;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  const prev = storedPrivateLocation || {};
  const unchanged = prev.lat === lat && prev.lng === lng;
  if (unchanged && Number.isFinite(prev.fuzzedLat) && Number.isFinite(prev.fuzzedLng)) {
    return { lat: prev.fuzzedLat, lng: prev.fuzzedLng };
  }
  return fuzzyGeolocation({ lat, lng });
};

/**
 * Reduce a stored geocoded address to the coarse locality that is safe to publish.
 *
 * Addresses end "…, City, <state> [ZIP]", optionally with a trailing country, where <state> is
 * either a two-letter code (Google Places) or a full name (Mapbox, which is what this marketplace
 * uses). Anchors on the STATE segment rather than a fixed index, because counting from the front
 * mis-reads both "Springfield, IL 62704, USA" and "…, Apt 4B, Springfield, IL, USA" (Review #57.6).
 *
 * Anything that doesn't fit returns null — and a null publishes NOTHING, which is the safe
 * direction: better a card with no city than a card leaking a street.
 *
 * @param {string} address the full stored address
 * @returns {{city: string, state: string}|null}
 */
export const parseCoarseLocation = address => {
  if (typeof address !== 'string') {
    return null;
  }
  let parts = address
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);

  const COUNTRY = /^(usa|us|u\.s\.a?\.?|united states( of america)?)$/i;
  if (parts.length && COUNTRY.test(parts[parts.length - 1])) {
    parts = parts.slice(0, -1);
  }
  if (parts.length < 2) {
    return null;
  }

  // Strip a trailing ZIP, then accept either a postal code or a spelled-out state name.
  const last = parts[parts.length - 1].replace(/\s+\d{5}(-\d{4})?$/, '').trim();
  const state = /^[A-Za-z]{2}$/.test(last)
    ? last.toUpperCase()
    : US_STATE_CODES[last.toLowerCase()] || null;
  if (!state) {
    return null;
  }

  // A two-segment value has no city SLOT: the part before the state is the street line itself, so
  // "One Main Street, MA", "Broadway, NY" and "Infinite Loop, CA" would all publish a street as the
  // city. A keyword list cannot reliably tell those from a genuine "Springfield, IL", so this fails
  // closed instead. Real geocoded pickup addresses from Mapbox always carry street + city + state
  // (+ country), so nothing legitimate is lost — and publishing nothing is the safe failure.
  if (parts.length < 3) {
    return null;
  }
  const city = parts[parts.length - 2];
  // Belt and braces for the >=3 case, where the city slot can still hold a unit line
  // ("742 Evergreen Terrace, Building A, MA"): reject any digit or street/unit keyword.
  const STREETY = /\d|\b(st|street|ave|avenue|rd|road|blvd|boulevard|ln|lane|dr|drive|ct|court|way|terrace|ter|pl|place|hwy|highway|apt|apartment|suite|ste|unit|floor|fl|bldg|building|po box)\b/i;
  if (!city || STREETY.test(city)) {
    return null;
  }
  return { city, state };
};

/**
 * The public label for a job's location: "Springfield, IL", or just the city if the state is
 * missing. Returns null when there is nothing safe to show.
 * @param {{city?: string, state?: string}} coarse a publicData.location value
 * @returns {string|null}
 */
export const formatCoarseLocation = coarse => {
  const city = coarse?.city;
  if (typeof city !== 'string' || !city.trim()) {
    return null;
  }
  const state = typeof coarse?.state === 'string' ? coarse.state.trim() : '';
  return state ? `${city.trim()}, ${state}` : city.trim();
};

/**
 * Build the pickup-location half of a listing update, applying this marketplace's whole
 * location-privacy model in ONE place.
 *
 * WHERE THE PRECISE LOCATION GOES, AND WHY.
 *
 * publicData and geolocation are world-readable: anyone can call `listings.query` with the client id
 * from our JS bundle — no login, no listing URL — and read them for every job. So the street address
 * and the true coordinate go to privateData (author-only), and only a coarse "City, ST" is
 * published. The stored geolocation is fuzzed (~500m) with a REUSED offset, so re-saving an
 * unchanged address never publishes a fresh sample that would let repeated saves triangulate the
 * real point. The winning hauler gets the real address from /api/listing-address, which checks they
 * actually won the job.
 *
 * Two callers write a pickup location — the wizard's Location tab and the no-signup guest post — and
 * they MUST agree. Duplicating the rules would eventually leak a street address from whichever copy
 * fell behind, so both call this.
 *
 * @param {Object} params
 * @param {string} params.address full geocoded address
 * @param {string} [params.building] unit/apartment, kept author-only with the street
 * @param {{lat: number, lng: number}} params.origin the TRUE coordinate
 * @param {Object} [params.existingPrivateLocation] listing's current privateData.location, if any
 * @returns {{privateLocation: Object, publicLocation: Object|null, fuzzed: Object|null}|null}
 */
export const buildPickupLocationUpdate = ({
  address,
  building = '',
  origin,
  existingPrivateLocation,
}) => {
  if (
    typeof address !== 'string' ||
    !Number.isFinite(origin?.lat) ||
    !Number.isFinite(origin?.lng)
  ) {
    return null;
  }

  const privateLocation = { address, building, lat: origin.lat, lng: origin.lng };

  // An unparseable address publishes NOTHING rather than falling back to the full string — a card
  // with no city is a much better failure than a card leaking a street.
  const coarse = parseCoarseLocation(address);

  const fuzzed = stableFuzzyGeolocation(
    { lat: origin.lat, lng: origin.lng },
    existingPrivateLocation
  );
  if (fuzzed) {
    privateLocation.fuzzedLat = fuzzed.lat;
    privateLocation.fuzzedLng = fuzzed.lng;
  }

  return { privateLocation, publicLocation: coarse ? { ...coarse } : null, fuzzed };
};
